import type {
  BffCrmPersonLuciConversationsRetrieve2Response,
  BffCrmPersonLuciConversationsRetrieveResponse,
  BffCrmPersonLuciMessagesCreateBodyParams,
  BffCrmPersonLuciMessagesCreateResponse,
  BffCrmPersonLuciMessagesFeedbackCreateBodyParams,
} from '@fireflyhealth/api'
import {
  bffCrmPersonLuciConversations,
  bffCrmPersonLuciConversationsRetrieve2,
  bffCrmPersonLuciMessages,
  bffCrmPersonLuciMessagesFeedback,
} from '@fireflyhealth/api'
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { replaceEqualDeep, useQueryClient } from '@tanstack/react-query'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { ANSWER_POLL_INTERVAL_MILLIS } from './constants'

type LuciThread = BffCrmPersonLuciConversationsRetrieve2Response
export type LuciMessage = LuciThread['messages'][number]
export type LuciConversation =
  BffCrmPersonLuciConversationsRetrieveResponse['conversations'][number]
type LuciAsk = BffCrmPersonLuciMessagesCreateResponse
type LuciFeedback = NonNullable<LuciMessage['feedback']>
export type LuciRating = LuciFeedback['rating']

export interface LuciCitation {
  id: string
  label: string
}

const SETTLED_STATUSES: LuciMessage['status'][] = ['complete', 'skipped', 'failed']

/** Whether the job behind an assistant turn has stopped, so polling can too. */
export const isSettled = (status: LuciMessage['status'] | undefined) =>
  status != null && SETTLED_STATUSES.includes(status)

/** The citations a turn stores; the column is untyped JSON, so each entry is checked. */
export const citationsOf = (message: LuciMessage): LuciCitation[] =>
  Array.isArray(message.citations) ? message.citations.filter(isCitation) : []

const isCitation = (value: unknown): value is LuciCitation => {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<LuciCitation>
  return typeof candidate.id === 'string' && typeof candidate.label === 'string'
}

const conversationsKey = (personId: number) =>
  bffCrmPersonLuciConversations.retrieveQueryKey({ person_id: personId })

const threadKey = (personId: number, conversationId: number) =>
  bffCrmPersonLuciConversationsRetrieve2.getQueryKey({
    person_id: personId,
    conversation_id: conversationId,
  })

export const useLuciConversations = (personId: number) =>
  useQuery<LuciConversation[]>(
    conversationsKey(personId),
    () =>
      bffCrmPersonLuciConversations
        .retrieve({ person_id: personId })
        .then(response => response.data.conversations),
    {},
    { error: 'Could not load Luci conversations' },
  )

const hasUnsettledTurn = (thread: LuciThread | undefined) =>
  thread?.messages.some(message => message.role === 'assistant' && !isSettled(message.status)) ??
  false

/**
 * Whether the thread still owes an answer: polling runs and the composer waits while it does. A
 * failing re-read ends both; the error toast is the signal, not a spinner.
 */
export const isAwaitingAnswer = (thread: LuciThread | undefined, error: unknown) =>
  error == null && hasUnsettledTurn(thread)

/** The conversation's turns, re-read on a cadence while any answer is still being written. */
export const useLuciThread = (personId: number, conversationId: number | null) => {
  const queryKey =
    conversationId == null ? ['luciThread', personId, null] : threadKey(personId, conversationId)
  return useQuery<LuciThread>(
    queryKey,
    () =>
      bffCrmPersonLuciConversationsRetrieve2
        .get({ person_id: personId, conversation_id: Number(conversationId) })
        .then(response => response.data),
    {
      enabled: conversationId != null,
      retry: 1,
      // v5 types structural sharing over `unknown`; both sides are this query's own data.
      structuralSharing: (held, fetched) =>
        mergeThread(held as LuciThread | undefined, fetched as LuciThread),
      refetchInterval: query =>
        isAwaitingAnswer(query.state.data, query.state.error) ? ANSWER_POLL_INTERVAL_MILLIS : false,
    },
    { error: 'Could not load the conversation' },
  )
}

export interface LuciNudge {
  m: number
  n: number
}

/** The signal payload: message id and trace version, nothing else rides on it. */
export const asNudge = (value: unknown): LuciNudge | null => {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Partial<LuciNudge>
  if (typeof candidate.m !== 'number' || typeof candidate.n !== 'number') return null
  return { m: candidate.m, n: candidate.n }
}

/** Re-reads one turn when a nudge says its trace moved past what the thread holds. */
export const refreshNudgedTurn = async (
  queryClient: QueryClient,
  personId: number,
  conversationId: number,
  nudge: LuciNudge,
) => {
  const key = threadKey(personId, conversationId)
  const held = heldTurn(queryClient, key, nudge.m)
  if (held == null || isSettled(held.status)) return
  if (nudge.n <= held.trace_version) return
  let fresh = await readTurn(personId, nudge.m)
  // The client shares a read already in flight for the turn, so a nudge that arrived during it
  // can get a response from before its write; one more read catches up.
  if (!isSettled(fresh.status) && fresh.trace_version < nudge.n)
    fresh = await readTurn(personId, nudge.m)
  queryClient.setQueryData<LuciThread>(key, thread =>
    thread == null
      ? thread
      : { ...thread, messages: thread.messages.map(turn => newerOf(turn, fresh)) },
  )
}

const heldTurn = (queryClient: QueryClient, key: QueryKey, messageId: number) =>
  queryClient.getQueryData<LuciThread>(key)?.messages.find(turn => turn.id === messageId)

const readTurn = async (personId: number, messageId: number) => {
  const response = await bffCrmPersonLuciMessages.retrieve({
    person_id: personId,
    message_id: messageId,
  })
  return response.data
}

// The poll and the nudge re-reads overlap, so every write into the thread, a poll response
// included, keeps each turn under the same rule.
const mergeThread = (held: LuciThread | undefined, fetched: LuciThread): LuciThread => {
  if (held == null) return fetched
  const heldById = new Map(held.messages.map(turn => [turn.id, turn]))
  const messages = fetched.messages.map(turn => newerOf(heldById.get(turn.id) ?? turn, turn))
  // Unchanged data keeps its identity, so an idle poll re-renders nothing.
  return replaceEqualDeep(held, { ...fetched, messages })
}

// A settled turn never regresses to an in-flight read (its version reads 0); between two settled
// reads the fresh one wins, which is what carries a verdict; otherwise the trace version says
// which read is current.
const newerOf = (held: LuciMessage, fresh: LuciMessage) => {
  if (held.id !== fresh.id) return held
  if (isSettled(held.status)) return isSettled(fresh.status) ? fresh : held
  if (isSettled(fresh.status)) return fresh
  return fresh.trace_version >= held.trace_version ? fresh : held
}

interface AskVariables {
  question: string
  conversationId: number | null
}

/** Records a question and its pending answer, and seeds the thread so both show at once. */
export const useAskLuci = (personId: number) => {
  const queryClient = useQueryClient()
  return useMutation<LuciAsk, unknown, AskVariables>(
    ({ question, conversationId }) => {
      const body: BffCrmPersonLuciMessagesCreateBodyParams = { question }
      if (conversationId != null) body.conversation_id = conversationId
      return bffCrmPersonLuciMessages
        .create({ person_id: personId }, { data: body })
        .then(response => response.data)
    },
    {
      onSuccess: asked => {
        queryClient.setQueryData<LuciThread>(
          threadKey(personId, asked.conversation.id),
          thread => ({
            conversation: asked.conversation,
            messages: [...(thread?.messages ?? []), asked.question, asked.answer],
          }),
        )
        queryClient.setQueryData<LuciConversation[]>(conversationsKey(personId), conversations => [
          asked.conversation,
          ...(conversations ?? []).filter(
            conversation => conversation.id !== asked.conversation.id,
          ),
        ])
        // Re-read after the write, so the cache holds what the server stored, not the seed.
        void queryClient.invalidateQueries({ queryKey: threadKey(personId, asked.conversation.id) })
        void queryClient.invalidateQueries({ queryKey: conversationsKey(personId) })
      },
    },
    { error: 'Luci could not take that question' },
  )
}

interface RateVariables {
  messageId: number
  rating: LuciRating
  correction: string | null
}

/** Sends the whole verdict on one answer and shows it at once; a rejected one is taken back. */
export const useRateAnswer = (personId: number, conversationId: number) => {
  const queryClient = useQueryClient()
  const key = threadKey(personId, conversationId)
  // The thread merge keeps a settled turn unless a settled re-read replaces it, which this is.
  const writeFeedback = (messageId: number, feedback: LuciFeedback | null) =>
    queryClient.setQueryData<LuciThread>(key, thread =>
      thread == null
        ? thread
        : {
            ...thread,
            messages: thread.messages.map(turn =>
              turn.id === messageId ? { ...turn, feedback } : turn,
            ),
          },
    )
  const carries = (messageId: number, feedback: LuciFeedback) =>
    heldTurn(queryClient, key, messageId)?.feedback?.updated_at === feedback.updated_at
  return useMutation<LuciFeedback, unknown, RateVariables, LuciFeedback | null>(
    ({ messageId, rating, correction }) => {
      const body: BffCrmPersonLuciMessagesFeedbackCreateBodyParams = { rating, correction }
      return bffCrmPersonLuciMessagesFeedback
        .create({ person_id: personId, message_id: messageId }, { data: body })
        .then(response => response.data)
    },
    {
      onMutate: async ({ messageId, rating, correction }) => {
        // A poll already in flight read the turn before this verdict; its response must not land.
        await queryClient.cancelQueries({ queryKey: key })
        const previous = heldTurn(queryClient, key, messageId)?.feedback ?? null
        writeFeedback(messageId, {
          id: previous?.id ?? 0,
          message: messageId,
          rating,
          correction,
          updated_at: new Date().toISOString(),
        })
        return previous
      },
      onSuccess: async (feedback, { messageId }) => {
        writeFeedback(messageId, feedback)
        // Re-read after the write, as the ask does; a read still in flight is cancelled with it.
        // The client can serve a read that began before the write, so one more read catches up.
        await queryClient.invalidateQueries({ queryKey: key })
        if (carries(messageId, feedback)) return
        writeFeedback(messageId, feedback)
        await queryClient.invalidateQueries({ queryKey: key })
      },
      onError: (_error, { messageId }, previous) => writeFeedback(messageId, previous ?? null),
    },
    { error: 'Could not send feedback on that answer' },
  )
}
